// useHotLeads.js
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import LeadDbService from "../components/Services/LeadDbService";
import LocationService from "../components/Services/LocationService";
import teamService from "../components/Services/TeamService";
import { leadStatusMap } from "../components/Constant/Constant";
import { showErrorToast } from "../components/Constant/Notification";

const useHotLeads = () => {
  const [leads, setLeads] = useState([]);

  const [filterVisible, setFilterVisible] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [interestLocationTags, setInterestedLocationTags] = useState([]);
  const [optionLocation, setOptionLocation] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedCountLead , setSelectedCountLead] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [nextPageToken, setNextPageToken] = useState(null);
  const [prevPageToken, setPrevPageToken] = useState(null);
  const [hasMoreLeads, setHasMoreLeads] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [leadCache, setLeadCache] = useState({});
  const [lastUpdateLeadData, setLastUpdateLeadData] = useState();
  const [nextUpdateLeadData, setNextUpdateLeadData] = useState();
  const navigate = useNavigate();
  const userData = JSON.parse(localStorage.getItem("userData"));
  const userRole = userData?.user?.role;
  const teamIds = userData?.teamId || [];

  const menuRef = useRef(null);

  const formatDate = (date) => {
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const year = d.getFullYear();
    return `${month}-${day}-${year}`;
  };

  const formattedStartDate = startDate ? formatDate(startDate) : "";
  const formattedEndDate = endDate ? formatDate(endDate) : "";

  const getLead = async (pageToken = null, direction = "next", limitOverride = null, overridePage = null, forceReload = false) => {
    const targetPage = overridePage ?? currentPage;

    if (startDate && !endDate) {
      showErrorToast("End Date is required when Start Date is selected.");
      return;
    }

    if (!forceReload && leadCache[targetPage]) {
      const cached = leadCache[targetPage];
      setLeads(cached.leads);
      setNextPageToken(cached.next);
      setPrevPageToken(cached.prev);
      setHasMoreLeads(!!cached.next);
      setCurrentPage(targetPage);
      return;
    }

    setLoading(true);

    try {
      const body = {
        searchKey: searchQuery || null,
        role: userRole || null,
        teamIds: Array.isArray(teamIds) ? teamIds : [],
        status: selectedStatus || null,
        quality: selectedLevel?.label || null,
        location: interestLocationTags?.value || null,
        filterTeamId: selectedTeam || null,
        user: selectedUser || null,
        startDate: formattedStartDate || null,
        endDate: formattedEndDate || null,
        limit: limitOverride ?? pageSize,
        next: direction === "next" ? pageToken : null,
        previous: direction === "previous" ? pageToken : null,
        direction,
      };

      const response = await LeadDbService.getLeadData(body);
      const newLeads = response.data.leads;
      const newNext = response.data.next;
      const newPrev = response.data.prev;

      setLeadCache((prev) => ({
        ...prev,
        [targetPage]: { leads: newLeads, next: newNext, prev: newPrev },
      }));

      setLeads(newLeads);
      setNextPageToken(newNext);
      setPrevPageToken(newPrev);
      setHasMoreLeads(!!newNext);
      setCurrentPage(targetPage);
    } catch (error) {
      console.error("Error in getLead:", error);
    } finally {
      setLoading(false);
    }
  };

 



  const applyFilter = () => {
    setLeadCache({});
    setCurrentPage(1);
    getLead(null, "reset", pageSize, 1, true);
    setFilterVisible(false);
  };

  const clearFilterData = () => {
    setSelectedStatus("");
    setSelectedLevel(null);
    setSelectedCountLead(null)
    setInterestedLocationTags([]);
    setSelectedTeam("");
    setSelectedUser("");
    setStartDate("");
    setEndDate("");
    getLead(null, "reset", pageSize, 1, true);
  };

  const handlePageChange = (tokenOrValue, direction) => {
    if (direction === "size") {
      const newPageSize = parseInt(tokenOrValue, 10);
      setPageSize(newPageSize);
      setCurrentPage(1);
      setLeadCache({});
      setNextPageToken(null);
      setPrevPageToken(null);
      getLead(null, "reset", newPageSize, 1);
    } else if (direction === "next") {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      getLead(nextPageToken, "next", pageSize, nextPage);
    } else if (direction === "previous") {
      const prevPage = Math.max(currentPage - 1, 1);
      setCurrentPage(prevPage);
      getLead(prevPageToken, "previous", pageSize, prevPage);
    }
  };

  const handleSearchLead = () => {
    setLeadCache({});
    setCurrentPage(1);
    getLead(null, "reset", pageSize, 1, true);
  };

  const handleLeadInfo = (lead) => {
    const path = {
      Admin: `/admin/lead-info/${lead.id}`,
      CMO: `/cmo/lead-info/${lead.id}`,
      MD: `/md/lead-info/${lead.id}`,
      BOD: `/bod/lead-info/${lead.id}`,
    }[userRole] || "";
    navigate(path, { state: { lead } });
  };



  useEffect(() => {
    getLead(null, "reset", pageSize, 1, true);

  }, [pageSize]);


  return {
    leads,
    filterVisible,
    setFilterVisible,
    selectedStatus,
    setSelectedStatus,
    selectedLevel,
    setSelectedLevel,
    interestLocationTags,
    setInterestedLocationTags,
    optionLocation,
    teams,
    users,
    selectedUser,
    setSelectedUser,
    selectedRows,
    setSelectedRows,
    selectedTeam,
    setSelectedTeam,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    searchQuery,
    setSearchQuery,
    selectedCountLead,
    setSelectedCountLead,
    loading,
    isLoading,
    currentPage,
    nextPageToken,
    prevPageToken,
    hasMoreLeads,
    pageSize,
    lastUpdateLeadData,
    nextUpdateLeadData,
    menuRef,
    userRole,
    applyFilter,
    clearFilterData,
    handlePageChange,
    handleSearchLead,
    handleLeadInfo,

  };
};

export default useHotLeads;